
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { fetchAllCategories, formatCategoryDisplay } from "@/services/business/categoryService";
import { BusinessCategoryString } from "@/types/business";
import { Category } from "@/services/business/categoryService";
import { Skeleton } from "@/components/ui/skeleton";

interface CategoryFilterProps {
  activeCategory: BusinessCategoryString | undefined;
  setActiveCategory: (category: string | undefined) => void;
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  activeCategory,
  setActiveCategory,
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedCategories = await fetchAllCategories();
        setCategories(fetchedCategories);
      } catch (err: any) {
        console.error("Error loading categories:", err);
        setError("Failed to load categories.");
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const renderCategories = () => {
    if (isLoading) {
      return (
        <div className="flex flex-wrap gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-9 w-28" />
        </div>
      );
    }

    if (error) {
      return <div className="text-red-500">{error}</div>;
    }
    
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          key="all"
          variant={!activeCategory ? "default" : "outline"}
          size="sm"
          onClick={() => setActiveCategory(undefined)}
          className={!activeCategory ? "bg-eco-500 hover:bg-eco-600" : ""}
        >
          All Categories
        </Button>

        {categories.map((category) => (
          <Button
            key={category.id}
            variant={activeCategory === category.id ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveCategory(category.id)}
            className={
              activeCategory === category.id ? "bg-eco-500 hover:bg-eco-600" : ""
            }
          >
            {formatCategoryDisplay(category.id)}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-medium mb-4">Filter by Category</h3>
      {renderCategories()}
    </div>
  );
};

export default CategoryFilter;
